import { useContext, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment-jalaali";
import Spinner from "react-bootstrap/Spinner";
import { Trash } from "iconsax-react";
import { useTranslation } from "react-i18next";
import { ToastContext } from "../../../../component/global/ToastContext";
import ConfirmModal from "../../../../component/global/ConfirmModal";
import { del, post } from "../../../../controlers/global/ConfigRequest";
import {
    DELETE_USER_COMPANY,
    SET_CLUB_MEMBER_USER_GROUP,
} from "../../../../helperData/ApiAddress";
import {
    formatCount,
    formatPhone,
    memberDisplayName,
    memberInitials,
} from "./helpers";

function groupNameById(userGroups, groupId) {
    const match = userGroups.find((group) => String(group.id) === String(groupId));
    return match?.name || "";
}

export default function CustomerClubTableRow({
    data,
    setRefresh,
    userGroups = [],
    locale = "fa-IR",
    onOpen,
}) {
    const [deleteBusy, setDeleteBusy] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [groupLoading, setGroupLoading] = useState(false);
    const [pendingGroupId, setPendingGroupId] = useState("");
    const [selectedGroupId, setSelectedGroupId] = useState(
        data.user_group ?? data.user?.user_group ?? ""
    );
    const { t } = useTranslation();
    const show = useContext(ToastContext);
    const name = memberDisplayName(data);
    const joined = data.created_at
        ? moment(data.created_at).format("jYYYY/jMM/jDD")
        : "—";

    const deleteUser = () => {
        setDeleteBusy(true);
        del(DELETE_USER_COMPANY.replace("{id}/", data.id))
            .then(() => {
                show(
                    t(
                        "Src.Pages.CustomerClub.Components.UserManagment.TableRow.Success"
                    ),
                    "success"
                );
                setConfirmDelete(false);
                setRefresh((prevState) => !prevState);
            })
            .catch(() => {
                show(
                    t(
                        "Src.Pages.CustomerClub.Components.UserManagment.TableRow.Danger"
                    ),
                    "danger"
                );
            })
            .finally(() => setDeleteBusy(false));
    };

    const applyGroupChange = () => {
        const nextGroupId = Number(pendingGroupId);
        if (!nextGroupId || nextGroupId === Number(selectedGroupId)) {
            setPendingGroupId("");
            return;
        }

        setGroupLoading(true);
        post(SET_CLUB_MEMBER_USER_GROUP.replace("{id}", data.id), {
            user_group: nextGroupId,
        })
            .then((response) => {
                setSelectedGroupId(
                    response.user_group ??
                        response.user?.user_group ??
                        nextGroupId
                );
                show(
                    t(
                        "Organizational.CustomerClub.Member.UserGroup.UpdateSuccess"
                    ),
                    "success"
                );
                setRefresh((prevState) => !prevState);
            })
            .catch(() => {
                show(
                    t(
                        "Organizational.CustomerClub.Member.UserGroup.UpdateError"
                    ),
                    "danger"
                );
            })
            .finally(() => {
                setGroupLoading(false);
                setPendingGroupId("");
            });
    };

    const isInteractive = (target) =>
        Boolean(
            target?.closest?.(
                "button, a, select, input, label, textarea, option"
            )
        );

    const openMember = (event) => {
        if (isInteractive(event.target)) return;
        onOpen?.(data);
    };

    const onRowKeyDown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        if (isInteractive(event.target)) return;
        event.preventDefault();
        onOpen?.(data);
    };

    return (
        <>
            <tr
                className={deleteBusy ? "is-busy member-row" : "member-row"}
                tabIndex={0}
                onClick={openMember}
                onKeyDown={onRowKeyDown}
                title={t("Organizational.CustomerClub.Members.Dossier.OpenRow")}
            >
                <td data-label={t("Organizational.CustomerClub.Members.Col.Member")}>
                    <div className="member-identity">
                        <span className="member-avatar" aria-hidden="true">
                            {memberInitials(name)}
                        </span>
                        <span className="member-identity-copy">
                            <span className="member-name">{name}</span>
                            <span className="member-phone" dir="ltr">
                                {formatPhone(data.user?.phone_number)}
                            </span>
                            <span className="member-mobile-meta">
                                <span className="member-joined tabular-nums">{joined}</span>
                                <span className="member-mobile-stats">
                                    {t("Organizational.CustomerClub.Members.Col.Messages")}{" "}
                                    {formatCount(data.messages_count, locale)}
                                    <span aria-hidden="true"> · </span>
                                    {t("Organizational.CustomerClub.Members.Col.Ideas")}{" "}
                                    {formatCount(data.ideas_count, locale)}
                                    <span aria-hidden="true"> · </span>
                                    {t("Organizational.CustomerClub.Members.Col.Coins")}{" "}
                                    {formatCount(data.coins_earned, locale)}
                                </span>
                            </span>
                        </span>
                    </div>
                </td>
                <td data-label={t("Organizational.CustomerClub.Members.Col.Joined")}>
                    <span className="member-joined tabular-nums">{joined}</span>
                </td>
                <td data-label={t("Organizational.CustomerClub.Members.Col.Group")}>
                    <label className="visually-hidden" htmlFor={`member-group-${data.id}`}>
                        {t("Organizational.CustomerClub.Member.UserGroup.Label")}
                    </label>
                    <div
                        className="member-group-control"
                        onClick={(event) => event.stopPropagation()}
                    >
                        <select
                            id={`member-group-${data.id}`}
                            className="member-group-select"
                            value={selectedGroupId || ""}
                            disabled={groupLoading || userGroups.length === 0}
                            onChange={(event) =>
                                setPendingGroupId(event.target.value)
                            }
                        >
                            <option value="" disabled>
                                {t(
                                    "Organizational.CustomerClub.Member.UserGroup.Placeholder"
                                )}
                            </option>
                            {userGroups.map((group) => (
                                <option key={group.id} value={group.id}>
                                    {group.name}
                                </option>
                            ))}
                        </select>
                        {groupLoading ? (
                            <Spinner animation="border" size="sm" />
                        ) : null}
                    </div>
                </td>
                <td
                    className="is-metric"
                    data-label={t("Organizational.CustomerClub.Members.Col.Messages")}
                    title={t("Organizational.CustomerClub.Members.Hint.Messages")}
                >
                    <span className="tabular-nums">
                        {formatCount(data.messages_count, locale)}
                    </span>
                </td>
                <td
                    className="is-metric"
                    data-label={t("Organizational.CustomerClub.Members.Col.Ideas")}
                    title={t("Organizational.CustomerClub.Members.Hint.Ideas")}
                >
                    <span className="tabular-nums">
                        {formatCount(data.ideas_count, locale)}
                    </span>
                </td>
                <td
                    className="is-metric"
                    data-label={t("Organizational.CustomerClub.Members.Col.Referrals")}
                    title={t("Organizational.CustomerClub.Members.Hint.Referrals")}
                >
                    <span className="tabular-nums">
                        {formatCount(data.referral_opens, locale)}
                    </span>
                </td>
                <td
                    className="is-metric"
                    data-label={t("Organizational.CustomerClub.Members.Col.Coins")}
                    title={t("Organizational.CustomerClub.Members.Hint.Coins")}
                >
                    <span className="tabular-nums">
                        {formatCount(data.coins_earned, locale)}
                    </span>
                </td>
                <td
                    className="is-metric"
                    data-label={t("Organizational.CustomerClub.Members.Col.Views")}
                    title={t("Organizational.CustomerClub.Members.Hint.Views")}
                >
                    <span className="tabular-nums">
                        {formatCount(data.content_views, locale)}
                    </span>
                </td>
                <td data-label={t("Organizational.CustomerClub.Members.Col.Actions")}>
                    <button
                        type="button"
                        className="member-icon-btn member-icon-btn-danger"
                        onClick={(event) => {
                            event.stopPropagation();
                            setConfirmDelete(true);
                        }}
                        disabled={deleteBusy}
                        aria-label={t("Organizational.CustomerClub.Members.Delete")}
                    >
                        {deleteBusy ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            <Trash size={16} aria-hidden="true" />
                        )}
                    </button>
                </td>
            </tr>
            {createPortal(
                <>
                    <ConfirmModal
                        show={Boolean(pendingGroupId)}
                        onHide={() => setPendingGroupId("")}
                        onConfirm={applyGroupChange}
                        title={t("Organizational.CustomerClub.Members.Group.Title")}
                        description={t(
                            "Organizational.CustomerClub.Members.Group.Body",
                            {
                                name,
                                group: groupNameById(userGroups, pendingGroupId),
                            }
                        )}
                        btnTitle={t(
                            "Organizational.CustomerClub.Members.Group.Confirm"
                        )}
                    />
                    <ConfirmModal
                        show={confirmDelete}
                        onHide={() => setConfirmDelete(false)}
                        onConfirm={deleteUser}
                        title={t("Organizational.CustomerClub.Members.Delete.Title")}
                        description={t(
                            "Organizational.CustomerClub.Members.Delete.Body",
                            {
                                name,
                            }
                        )}
                        btnTitle={t("Organizational.CustomerClub.Members.Delete")}
                    />
                </>,
                document.body
            )}
        </>
    );
}
